import type { ManagerTopBrand } from './types'

function formatRub(n: number | null): string {
  if (n === null || n === 0) return '—'
  if (Math.abs(n) >= 1_000_000) return `₽ ${(n / 1_000_000).toFixed(1)}M`
  if (Math.abs(n) >= 1_000) return `₽ ${Math.round(n / 1_000)}K`
  return `₽ ${n.toFixed(2)}`
}

/** Топ брендов клиентов менеджера за период. Заменил TopPartsTable
 *  по запросу Сергея 28.04: бренд важнее парт-номера для оценки рынка
 *  и для дальнейших разговоров с поставщиками («у нас по Sunon
 *  отгружено 100 позиций — попроси скидку»).
 *
 *  Таблица показывает по каждому бренду:
 *    - сколько позиций брал в работу за период;
 *    - сколько из них реально оформлены в заказ;
 *    - сумму заказа по бренду (orderedPrice * quantity);
 *    - среднюю цену за единицу в заказе. */
export function TopBrandsTable({ topBrands }: { topBrands: ManagerTopBrand[] }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4 h-full">
      <div className="text-sm text-gray-500 mb-3">Топ брендов</div>

      {topBrands.length === 0 ? (
        <p className="text-sm text-gray-400 py-4">Нет данных за период</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500">
                <th className="text-left font-normal pb-2">Бренд</th>
                <th className="text-right font-normal pb-2">Позиций</th>
                <th className="text-right font-normal pb-2">В заказе</th>
                <th className="text-right font-normal pb-2">Сумма</th>
                <th className="text-right font-normal pb-2">Ср. цена</th>
              </tr>
            </thead>
            <tbody>
              {topBrands.map((b, idx) => {
                // Прогресс-бар отношения orderedCount / positionsCount —
                // визуально показывает «насколько хорошо бренд конвертится в заказ».
                const convertionRatio = b.positionsCount > 0 ? b.orderedCount / b.positionsCount : 0
                return (
                  <tr key={`${b.brand ?? '__none__'}-${idx}`} className="border-t border-gray-100">
                    <td className="py-2 text-gray-900">{b.brand ?? 'Без бренда'}</td>
                    <td className="py-2 text-right tabular-nums text-gray-700">
                      {b.positionsCount}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      <div className="inline-flex items-center gap-1.5 justify-end">
                        <span className="text-gray-700">{b.orderedCount}</span>
                        <div className="w-12 h-1.5 bg-gray-100 rounded overflow-hidden">
                          <div
                            className="h-full bg-emerald-500"
                            style={{ width: `${Math.round(convertionRatio * 100)}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="py-2 text-right tabular-nums text-gray-900 font-medium">
                      {formatRub(b.orderedSumRub)}
                    </td>
                    <td className="py-2 text-right tabular-nums text-gray-500">
                      {formatRub(b.avgOrderedPriceRub)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
